import { useRouter } from 'next/router';
import Link from 'next/link';
import {
  IconButton, MenuItem, Chip, Avatar as MUIAvatar,
} from '@mui/material';
import { Edit } from '@mui/icons-material';
import {
  userIsAdmin, userIsPro, userIsProfileOwner, abbreviateNum,
} from 'lib/helpers';
import { ListOrProjectWithStages, MilestoneCounts } from 'lib/types';
import { ProfileUser } from 'lib/types/users';
import { useCurrentUser, useApi, useCurrentUserIsAuthed } from 'lib/contexts/ApplicationState';
import { useUserRoles } from 'lib/contexts/userRolesContext';
import { getAvatarOrPlaceholder, imageAvatarUrl } from 'lib/utils/imageUtils';
import { locationToSlug } from 'lib/utils/modelSlugUtils';
import { Avatar } from 'components/Avatar';
import { TotalsSummary } from 'components/TotalsSummary';
import { KeyRoles } from 'components/KeyRoles';
import { TopCollaborators } from 'components/TopCollaborators';
import { setCookie } from 'nookies';
import { DropdownMenu } from 'components/DropdownMenu';
import { SaveCreatorButton } from 'components/SaveToList';
import { SocialMediaRow } from 'components/SocialMediaRow';
import { PostServiceStats } from 'server/api/types';
import { USER_TYPES } from 'lib/constants';
import { useSnackbar } from 'notistack';
import { useEffect, useState, useCallback } from 'react';
import { trackClickSignupEvent, trackShareProfileEvent } from 'lib/hooks/useEventTracking';
import { useModal } from 'mui-modal-provider';
import { ShareButton } from 'components/ShareButton';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { ClaimProfileModal } from '../ClaimProfileModal';
import { JobsButton } from './Jobs/JobsButton';
import { TeamRequestButton } from './ClaimAccountButton';

interface Props {
  onEdit: () => void;
  profileUser: ProfileUser;
  milestoneCounts: MilestoneCounts | undefined;
  stats?: PostServiceStats;
  totalFollowers?: number;
}

export const UserSummary = ({
  onEdit, profileUser, milestoneCounts, stats, totalFollowers,
}: Props) => {
  const currentUser = useCurrentUser();
  const currentUserIsAuthed = useCurrentUserIsAuthed();
  const router = useRouter();
  const [
    userMilestoneCounts,
    setUserMilestoneCounts,
  ] = useState<MilestoneCounts | undefined>(milestoneCounts);
  const [
    profileStats,
    setProfileStats,
  ] = useState<PostServiceStats | undefined>(stats);
  const [
    userTotalFollowers,
    setUserTotalFollowers,
  ] = useState<number | undefined>(totalFollowers || 0);
  const [savedToLists, setSavedToLists] = useState<ListOrProjectWithStages[]>([]);
  const [savedToProjects, setSavedToProjects] = useState<ListOrProjectWithStages[]>([]);
  const isProfileUserCurrentUser = userIsProfileOwner(currentUser, profileUser);
  const profileUserRoles = useUserRoles();
  const API = useApi();
  const { enqueueSnackbar } = useSnackbar();
  const { showModal } = useModal();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Function to fetch list details for the profile user
  const fetchListDetails = useCallback(async () => {
    if (!currentUser || !profileUser?.id) {
      return;
    }

    try {
      const listDetails = await API.getCreatorsListDetails([profileUser.id]);
      const details = listDetails[profileUser.id];

      if (details) {
        setSavedToLists(details.savedToListsWithStages || []);
        setSavedToProjects(details.savedToProjectsWithStages || []);
      }
    } catch (err) {
      // Silently fail for list details fetching
    }
  }, [currentUser, profileUser?.id, API]);

  useEffect(() => {
    const getMilestoneCounts = async () => {
      try {
        const counts = await API.getAchievedMilestoneCount(profileUser.id);
        setUserMilestoneCounts(counts);
      } catch (err) {
        // noop - swallow errors
      }
    };

    /**
     * getStats is only called for logged-in users.
     * Anonymous users will have stats passed in via props so they are rendered
     * on the server for SEO.
     **/
    const getStats = async () => {
      try {
        const response = await API.getProfileStats(profileUser.id);
        setProfileStats(response);
      } catch (err) {
        // noop - swallow errors
      }
    };

    const getTotalFollowers = async () => {
      try {
        const total = await API.getTotalFollowers(profileUser.id);
        setUserTotalFollowers(total);
      } catch (err) {
        // noop - swallow errors
      }
    };

    if (currentUserIsAuthed && !profileStats) {
      getStats();
    }
    if (currentUserIsAuthed && !milestoneCounts) {
      getMilestoneCounts();
    }

    if (currentUserIsAuthed && !userTotalFollowers) {
      getTotalFollowers();
    }
  }, [currentUser, profileUser, currentUserIsAuthed, stats, milestoneCounts, totalFollowers]);

  // Fetch list details when component mounts or currentUser changes
  useEffect(() => {
    if (currentUser && profileUser?.id) {
      fetchListDetails();
    }
  }, [fetchListDetails, currentUser, profileUser?.id]);

  // Fetch metadata terms for admins
  const { data: metadataTerms } = useQuery({
    queryKey: ['getUserMetadata', profileUser.id],
    queryFn: () => API.getUserMetadata(profileUser.id),
    enabled: !!userIsAdmin(currentUser) && !!profileUser?.id,
  });

  const masquerade = () => {
    if (profileUser && userIsAdmin(currentUser)) {
      setCookie(null, 'masquerade', profileUser.id.toString());
      window.location.href = `/${profileUser.username}`;
    }
  };

  const goToAdmin = () => {
    if (profileUser && userIsAdmin(currentUser)) {
      router.push(`/admin/users?activeUser=${profileUser.id}`);
    }
  };

  const goToReplay = () => {
    if (profileUser && userIsAdmin(currentUser)) {
      router.push(`/replay?profileuser=${profileUser.username}`);
    }
  };

  const runReplay = async () => {
    if (profileUser && userIsAdmin(currentUser)) {
      await API.queueReplayRecalc(profileUser.id);
      enqueueSnackbar(`Replay cards enqueued for ${profileUser.name}`, { variant: 'success' });
    }
  };

  const handleClaimProfile = () => {
    if (!currentUser) {
      trackClickSignupEvent(API, `claim-profile-${profileUser.username}`);
      router.push(`/signup?welcome=${profileUser.username}`);
    } else {
      const modal: any = showModal(ClaimProfileModal, {
        onClose: () => modal.destroy(),
        username: profileUser.username,
      });
    }
  };

  if (!profileUser) {
    return <div>Loading...</div>;
  }

  return (
    <div className="border rounded-md text-center p-6 relative min-h-full lg:min-h-[480px] xl:min-h-[730px]">
      <div className="absolute top-1 right-1 place-items-center md:top-0 md:right-0">
        {isMobile && <ShareButton text="Check out this profile on Gondola" trackClick={() => trackShareProfileEvent(API, { content: profileUser.username })} />}
        {isProfileUserCurrentUser && (
          <IconButton
            color="primary"
            title="Edit Profile"
            aria-label="Edit Profile"
            onClick={onEdit}
            size="large"
          >
            <Edit />
          </IconButton>
        )}
        {userIsAdmin(currentUser) && !isProfileUserCurrentUser && (
          <div className="rounded-full text-orange border border-orange w-fit mt-2 md:mr-1">
            <DropdownMenu>
              <MenuItem onClick={masquerade}>
                Masquerade
              </MenuItem>
              <MenuItem onClick={goToAdmin}>
                Go to admin
              </MenuItem>
              <MenuItem onClick={goToReplay}>
                View Replay 2025
              </MenuItem>
              <MenuItem onClick={runReplay}>
                Run replay
              </MenuItem>
            </DropdownMenu>
          </div>
        )}
      </div>
      <div className="absolute top-2 left-2 flex items-center gap-2">
        <SaveCreatorButton
          userId={profileUser.id}
          savedToListsWithStages={savedToLists}
          savedToProjectsWithStages={savedToProjects}
          onListsUpdated={fetchListDetails}
        />
      </div>
      <div className="w-1/3 m-auto">
        <Avatar
          src={imageAvatarUrl(getAvatarOrPlaceholder(profileUser.avatarUrl, profileUser.id), 400)}
          userId={profileUser.id}
          isPro={userIsPro(profileUser)}
          alt={profileUser.name}
        />
      </div>
      <div className="mt-4">
        <h1 className="font-extrabold text-2xl">
          {profileUser.name}
        </h1>
        <h2>
          @
          {profileUser.username}
        </h2>
        {(!profileUser.termsAcknowledged
          && !profileUser.teamAccount
          && !profileUser.hasCredentials) && (
          <button
            type="button"
            className="btn-primary"
            onClick={handleClaimProfile}
          >
            Claim this profile
          </button>
        )}
        <div>
          {!!profileUser.location?.name
            && <Link className="text-sm" href={`/creators?geo=${locationToSlug(profileUser.location)}`}>{profileUser.location?.name}</Link>}
        </div>
        <div className="flex flex-col items-center space-y-2 my-4">
          {profileUser.userTypeId === USER_TYPES.company.id && (
            <JobsButton
              profileUser={profileUser}
            />
          )}
          {(profileUser.teamAccount?.id && currentUser?.selfServiceableAccounts) && (
            <TeamRequestButton
              teamAccount={profileUser.teamAccount}
            />
          )}
        </div>
        <SocialMediaRow profileUser={profileUser} alwaysCenter />
        {profileUser.blurb
          && (
            <div className="mt-4 text-left">
              <h2>
                {profileUser.blurb}
              </h2>
            </div>
          )}
        {(!profileUser.areStatsHidden) && (
          <div className="mt-4">
            <TotalsSummary totals={profileStats} totalFollowers={userTotalFollowers} />
          </div>
        )}
        {!!userMilestoneCounts?.achievedCount
          && userMilestoneCounts?.achievedCount > 0
          && profileUser.userTypeId !== USER_TYPES.company.id
          && profileUser.id === currentUser?.id
          && (
            <div className="flex justify-between items-center py-1">
              <div className="font-bold">Milestones Achieved</div>
              <div className="text-right">
                <Link
                  href={`/${profileUser.username}/milestones`}
                  className="underline"
                >
                  {`${userMilestoneCounts.achievedCount} of ${userMilestoneCounts.totalCount}`}
                </Link>
              </div>
            </div>
          )}

      </div>
      {profileUser.collaborators.length > 0 && (
        <div className="mt-4">
          <TopCollaborators profileUser={profileUser} />
        </div>
      )}
      <div className="mt-4">
        {
          (profileUserRoles && profileUserRoles.length > 0) && (
            <KeyRoles
              profileUserRoles={profileUserRoles}
              profileUser={profileUser}
              isProfileUserCurrentUser={isProfileUserCurrentUser}
            />
          )
        }
      </div>
      {userIsAdmin(currentUser) && metadataTerms && metadataTerms.length > 0 && (
        <div className="mt-4">
          <h6 className="font-bold text-lg mb-2 text-center">
            Metadata
            {' '}
            <span className="text-xs text-gray-400">ADMIN ONLY</span>
          </h6>
          <div className="flex flex-wrap gap-1 justify-center">
            {metadataTerms.map((item) => (
              <Chip
                key={item.term}
                className="bg-hintgray dark:bg-darkgray hover:bg-lightgray"
                sx={{ margin: '1px' }}
                avatar={<MUIAvatar className="bg-white dark:bg-appbg-dark" sx={{ backgroundColor: 'white' }}>{abbreviateNum(item.count, 0)}</MUIAvatar>}
                label={item.term}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
